import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import MetricsGrid from "./metrics/MetricsGrid"; // Make sure this path is correct!

const FeatureShowcase = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.1 });

  // A refresh counter for the graph.
  const [graphRefresh, setGraphRefresh] = useState(0);

  // Animation variants for the grid and text
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, duration: 0.5 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  // Feature text data
  const features = [
    {
      title: "Modular AI Integration",
      icon: (
        <svg
          className="w-6 h-6 text-[#5742bb]"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 00-1 1v6a1 1 0 001 1h3a1 1 0 001-1v-6a1 1 0 00-1-1h-3z" />
        </svg>
      ),
      description:
        "Businesses can adopt AI where it adds the most value without needing a full AI overhaul.",
    },
    {
      title: "Scalability & Adaptability",
      icon: (
        <svg
          className="w-6 h-6 text-[#5742bb]"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      description:
        "Designed for startups to enterprise-level adoption with seamless cloud-based expansion.",
    },
    {
      title: "Privacy & Security Focus",
      icon: (
        <svg
          className="w-6 h-6 text-[#5742bb]"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      description:
        "Zero-tracking AI models and end-to-end encryption for secure data processing.",
    },
    {
      title: "High-Performance Optimization",
      icon: (
        <svg
          className="w-6 h-6 text-[#5742bb]"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      description:
        "Neural acceleration, caching mechanisms, and lightweight AI implementations for minimal system impact.",
    },
    {
      title: "User-Centric & Ethical AI",
      icon: (
        <svg
          className="w-6 h-6 text-[#5742bb]"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      description:
        "Built-in bias detection, explainable AI (XAI), and adherence to responsible AI development standards.",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gray-50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Left column: Visual grid with metrics */}
          <motion.div variants={itemVariants} className="relative">
            <MetricsGrid
              refreshGraph={graphRefresh}
              setRefreshGraph={setGraphRefresh}
            />
          </motion.div>

          {/* Right column: Feature text */}
          <motion.div
            className="flex flex-col justify-center"
            variants={containerVariants}
          >
            <motion.div variants={textVariants} className="mb-3">
              <div className="uppercase text-[#5742bb] font-semibold tracking-wider text-sm">
                AI + INNOVATION
              </div>
            </motion.div>
            <motion.h2
              variants={textVariants}
              className="text-3xl md:text-4xl font-bold mb-8 text-gray-800"
            >
              Competitive Features <br className="hidden md:block" /> That
              Define Neuralward
            </motion.h2>
            <div className="space-y-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={textVariants}
                  className="flex items-start"
                >
                  <div className="mt-1 mr-4 flex-shrink-0 bg-[#5742bb]/10 p-2 rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div variants={textVariants} className="mt-10 flex justify-center md:justify-start">
              <Link to="/deployment/cloud" className="bg-[#5742bb] hover:bg-[#4835a0] text-white py-3 px-6 rounded-lg shadow-lg transition-all duration-300 flex items-center">
                <span>Explore Our AI Capabilities</span>
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
